import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../api/api';

// Step Components
import WelcomeStep from '../components/signup/WelcomeStep';
import BasicInfoStep from '../components/signup/BasicInfoStep';
import ResumeUploadStep from '../components/signup/ResumeUploadStep';
import SkillsStep from '../components/signup/SkillsStep';
import ExperienceStep from '../components/signup/ExperienceStep';
import EducationStep from '../components/signup/EducationStep';
import PreferencesStep from '../components/signup/PreferencesStep';
import CompletionStep from '../components/signup/CompletionStep';

// Shared UI Components
import ProgressIndicator from '../components/ui/ProgressIndicator';
import ConfettiEffect from '../components/ui/ConfettiEffect';
import ProfileStrengthMeter from '../components/ui/ProfileStrengthMeter';
import MotivationalQuote from '../components/ui/MotivationalQuote';
import RewardBadge from '../components/ui/RewardBadge';
import Header from '../components/Header';

const CandidateSignupPage = () => {
  const navigate = useNavigate();
  const { dispatch } = useContext(AuthContext);
  
  // State for managing steps
  const [currentStep, setCurrentStep] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [profileStrength, setProfileStrength] = useState(0);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [quote, setQuote] = useState('');
  
  // Form data state
  const [formData, setFormData] = useState({
    basicInfo: {
      name: '',
      email: '',
      phone: '',
      location: '',
      profilePicture: null
    },
    resume: {
      file: null,
      parsed: false
    },
    skills: [],
    experience: [],
    education: [],
    preferences: {
      jobTypes: [],
      industries: [],
      salaryExpectation: '',
      remotePreference: '',
      locations: []
    }
  });
  
  // Steps configuration
  const steps = [
    { id: 'welcome', title: 'Welcome', component: WelcomeStep },
    { id: 'basicInfo', title: 'Basic Info', component: BasicInfoStep },
    { id: 'resume', title: 'Resume', component: ResumeUploadStep },
    { id: 'skills', title: 'Skills', component: SkillsStep },
    { id: 'experience', title: 'Work Experience', component: ExperienceStep },
    { id: 'education', title: 'Education Background', component: EducationStep },
    { id: 'preferences', title: 'Job Preferences', component: PreferencesStep },
    { id: 'completion', title: 'All Done!', component: CompletionStep }
  ];
  
  // Calculate profile strength based on completed fields
  useEffect(() => {
    const calculateProfileStrength = () => {
      let strength = 0;
      const totalFields = 7; // Total number of major sections
      
      // Basic info check
      if (formData.basicInfo.name && formData.basicInfo.email && formData.basicInfo.phone) {
        strength += 1;
      }
      
      // Resume check
      if (formData.resume.file) {
        strength += 1;
      }
      
      // Skills check
      if (formData.skills.length > 0) {
        strength += 1;
      }
      
      // Experience check
      if (formData.experience.length > 0) {
        strength += 1;
      }
      
      // Education check
      if (formData.education.length > 0) {
        strength += 1;
      }
      
      // Preferences check
      if (formData.preferences.jobTypes.length > 0 && formData.preferences.industries.length > 0) {
        strength += 1;
      }
      
      // Avatar check
      if (selectedAvatar) {
        strength += 1;
      }
      
      setProfileStrength(Math.floor((strength / totalFields) * 100));
    };
    
    calculateProfileStrength();
  }, [formData, selectedAvatar]);
  
  // Fetch motivational quote when step changes
  useEffect(() => {
    const fetchQuote = async () => {
      try {
        // In a real app, this would call an API to get a motivational quote
        // For now, we'll use a simple array of quotes
        const quotes = [
          "The best way to predict your future is to create it! [ROCKET]",
          "Every expert was once a beginner. Keep going! ✨",
          "Your career journey starts with this step! 🌟",
          "Skills are the currency of the modern workplace. Keep adding to yours! 💪",
          "Your perfect job is out there waiting for you! 🔍",
          "You're doing great! Each detail helps employers find you! 👏",
          "Almost there! Your dream job is just around the corner! 🎯"
        ];
        
        setQuote(quotes[currentStep % quotes.length]);
      } catch (error) {
        console.error('Error fetching quote:', error);
      }
    };
    
    fetchQuote();
  }, [currentStep]);
  
  // Award badges based on progress
  useEffect(() => {
    const checkForBadges = () => {
      const newBadges = [...earnedBadges];
      
      // First step badge
      if (currentStep >= 1 && !earnedBadges.includes('first-step')) {
        newBadges.push('first-step');
      }
      
      // Profile halfway complete
      if (profileStrength >= 50 && !earnedBadges.includes('halfway')) {
        newBadges.push('halfway');
      }
      
      // Skills added
      if (formData.skills.length >= 5 && !earnedBadges.includes('skill-collector')) {
        newBadges.push('skill-collector');
      }
      
      // Resume uploaded
      if (formData.resume.file && !earnedBadges.includes('resume-ready')) {
        newBadges.push('resume-ready');
      }
      
      // All steps completed
      if (profileStrength === 100 && !earnedBadges.includes('all-star')) {
        newBadges.push('all-star');
      }
      
      // Only show confetti at the last step
      if (newBadges.length > earnedBadges.length) {
        setEarnedBadges(newBadges);
        
        // Only show confetti when profile is complete and we're on the last step
        if (profileStrength === 100 && currentStep === steps.length - 1) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 3000);
        }
      }
    };
    
    checkForBadges();
  }, [currentStep, profileStrength, formData, earnedBadges, steps.length]);
  
  // Handle next step
  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };
  
  // Handle previous step
  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };
  
  // Handle form data updates
  const updateFormData = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: typeof data === 'function' ? data(prev[section]) : data
    }));
  };

  // Handle avatar selection
  const handleAvatarSelection = (avatar) => {
    setSelectedAvatar(avatar);
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    try {
      // In a real app, this would submit the form data to your backend
      const response = await api.post('/candidate/signup', {
        ...formData,
        avatar: selectedAvatar
      });
      
      if (response.data.token) {
        localStorage.setItem('authToken', response.data.token);
        dispatch({ type: 'LOGIN', payload: { user: response.data.user } });
        navigate('/jobs');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };
  
  // Skip to next step
  const handleSkip = () => {
    handleNextStep();
  };
  
  // Render current step component
  const renderStep = () => {
    const StepComponent = steps[currentStep].component;
    return (
      <StepComponent
        formData={formData}
        updateFormData={updateFormData}
        onNext={handleNextStep}
        onPrev={handlePrevStep}
        onSkip={handleSkip}
        selectedAvatar={selectedAvatar}
        onAvatarSelect={handleAvatarSelection}
        onSubmit={handleSubmit}
        isLastStep={currentStep === steps.length - 1}
        isFirstStep={currentStep === 0}
      />
    );
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />
      <main className="flex-1 flex flex-col">
        <div className="w-full bg-gray-100 py-8 px-4 sm:px-6 lg:px-16 relative">
          {/* Subtle background pattern */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
          </div>
          
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Progress indicator */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <ProgressIndicator 
                steps={steps} 
                currentStep={currentStep} 
                onStepClick={(step) => step < currentStep && setCurrentStep(step)} 
              />
            </div>
            
            {/* Profile strength meter */}
            <div className="px-6 py-3 bg-white border-b border-gray-200 flex justify-between items-center">
              <ProfileStrengthMeter percentage={profileStrength} />
              
              {/* Avatar display if selected */}
              {selectedAvatar && (
                <div className="flex items-center">
                  <img 
                    src={selectedAvatar} 
                    alt="Your avatar" 
                    className="w-10 h-10 rounded-full border-2 border-yellow-400" 
                  />
                </div>
              )}
            </div>
            
            {/* Motivational quote */}
            <div className="px-6 py-3 bg-yellow-50 border-b border-yellow-100 text-center">
              <MotivationalQuote quote={quote} />
            </div>
            
            {/* Main content area */}
            <div className="p-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {renderStep()}
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Badges display */}
            {earnedBadges.length > 0 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Your Badges:</h3>
                <div className="flex flex-wrap gap-2">
                  {earnedBadges.map(badge => (
                    <RewardBadge key={badge} type={badge} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      
      {/* Confetti effect */}
      {showConfetti && <ConfettiEffect />}
    </div>
  );
};

export default CandidateSignupPage;
